// A seat's own profile. Deliberately small: their name, the store they work
// for, what their access covers, and a way to leave. Everything about the
// business itself stays on the owner's settings page.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Store, LogOut, Loader2, Check, UserCircle } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { updateProfile } from 'firebase/auth';
import { db, auth } from '../../config/firebase';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../hooks/useAuth';
import { useBusinessContext } from '../../hooks/useBusinessContext';
import { SEAT_ROLE_LABELS, SEAT_PERMISSIONS } from '../../types/business';

const PERMISSION_LABELS: Record<string, string> = {
  orders: 'Accept and fulfill orders',
  products: 'Add and edit products and inventory',
  hours: 'Update store hours',
  ownSales: 'See the sales you personally handled',
};

export function SeatProfilePage() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { businessId, isSeat, seatRole, loading: contextLoading } = useBusinessContext();

  const [storeName, setStoreName] = useState('');
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!businessId) return;
    getDoc(doc(db, 'businesses', businessId))
      .then((snap) => setStoreName(snap.data()?.name || snap.data()?.businessName || ''))
      .catch(() => setStoreName(''));
  }, [businessId]);

  useEffect(() => {
    if (user?.displayName) setDisplayName(user.displayName);
  }, [user?.displayName]);

  const handleSave = async () => {
    if (!auth.currentUser) return;
    setSaving(true);
    try {
      await updateProfile(auth.currentUser, { displayName: displayName.trim() });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      console.error('Failed to update profile:', err);
    } finally {
      setSaving(false);
    }
  };

  if (contextLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-6 w-6 text-primary animate-spin" />
      </div>
    );
  }

  if (!isSeat) {
    navigate('/business/settings', { replace: true });
    return null;
  }

  const permissions = seatRole ? SEAT_PERMISSIONS[seatRole] : [];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/business/dashboard')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-serif text-heading">My Profile</h1>
            <p className="text-xs text-body">Your account and access</p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                <UserCircle className="h-6 w-6" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-heading truncate">
                  {user?.displayName || user?.email}
                </p>
                <p className="text-xs text-body truncate">{user?.email}</p>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-heading">Display name</label>
              <Input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your name"
              />
              <p className="text-xs text-body">This is the name shown on orders you handle.</p>
            </div>

            <Button onClick={handleSave} disabled={saving} size="sm">
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  Saving
                </>
              ) : saved ? (
                <>
                  <Check className="h-4 w-4 mr-1.5" />
                  Saved
                </>
              ) : (
                'Save'
              )}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Store className="h-4 w-4 text-primary" />
              <h2 className="text-sm font-semibold text-heading">
                {storeName || 'Your store'}
              </h2>
            </div>
            <p className="text-xs text-body">
              You work here as{' '}
              <span className="font-medium text-heading">
                {seatRole ? SEAT_ROLE_LABELS[seatRole].label : 'Staff'}
              </span>
              .
            </p>

            <div className="space-y-2 pt-2 border-t border-gray-100">
              <p className="text-xs font-medium text-heading">Your access covers</p>
              {permissions.map((permission) => (
                <div key={permission} className="flex items-center gap-2">
                  <Check className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
                  <span className="text-xs text-body">{PERMISSION_LABELS[permission]}</span>
                </div>
              ))}
            </div>

            <div className="flex items-start gap-2 pt-2 border-t border-gray-100">
              <ShieldCheck className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-body leading-relaxed">
                The store's tax details, bank and payout information, and overall revenue stay with
                the owner. Ask them if you need your access changed.
              </p>
            </div>
          </CardContent>
        </Card>

        <Button
          variant="outline"
          className="w-full text-red-600 border-red-200 hover:bg-red-50"
          onClick={async () => {
            await signOut();
            navigate('/');
          }}
        >
          <LogOut className="h-4 w-4 mr-1.5" />
          Sign out
        </Button>
      </main>
    </div>
  );
}

export default SeatProfilePage;
